import { useState, lazy, Suspense } from 'react'
import { UploadCloud, X, Images, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import countryPageService from '../services/countryPageService'
import { cn, getInitials } from '@/lib/utils'
import useDropzone from '@/hooks/useDropzone'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

// UnsplashPicker sigue en MUI temporalmente — lazy-load para no inflar el bundle
const UnsplashPicker = lazy(() => import('./UnsplashPicker'))

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

/**
 * ImageUploader — drag-drop, preview, fallback con iniciales para avatares circulares.
 *
 * Mantiene la misma API que la versión MUI anterior para compatibilidad con callers.
 *
 * Cuando `circular`, fuerza aspect-square (1:1) con max-w del prop `height` —
 * antes se deformaba como elipse al tener width="100%" + height fijo.
 */
function ImageUploader({
  type,
  currentUrl = null,
  onUpload,
  onDelete,
  label = '',
  width = '100%',
  height = 200,
  circular = false,
  onUploadRequest = null,
  onDeleteRequest = null,
  disabled = false,
  emptyText = 'Arrastra una imagen o haz clic',
  helperText = 'JPG, PNG, WebP o GIF (max 5MB)',
  enableUnsplash = false,
  unsplashCategory = 'legal_hero',
  /** Iniciales/nombre para avatar fallback cuando circular y no hay imagen */
  fallbackName = '',
}) {
  const [isUploading, setIsUploading] = useState(false)
  // Arrastrar y soltar vive en `useDropzone`: la bandeja de la migración necesita lo mismo
  // con N archivos, y dos copias del mismo comportamiento se separan a la primera corrección.
  const { arrastrando: isDragOver, propsZona, propsInput } = useDropzone(
    (archivos) => handleUpload(archivos[0]),
    { multiple: false, accept: 'image/jpeg,image/png,image/webp,image/gif', disabled: disabled || isUploading }
  )
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [unsplashOpen, setUnsplashOpen] = useState(false)

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Formato no soportado. Usa JPG, PNG, WebP o GIF.')
      return false
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('La imagen excede el límite de 5MB.')
      return false
    }
    return true
  }

  const handleUpload = async (file) => {
    if (!validateFile(file)) return
    try {
      setIsUploading(true)
      let data
      if (onUploadRequest) {
        data = await onUploadRequest(file)
      } else {
        const response = await countryPageService.uploadImage(file, type)
        data = response?.data ?? response
        if (data && !data.url && data.data?.url) data = data.data
      }
      const url = data?.url
      if (!url || typeof url !== 'string' || (!url.startsWith('http') && !url.startsWith('/'))) {
        throw new Error('El servidor no devolvió una URL de imagen válida')
      }
      onUpload?.({ url, thumbnailUrl: data?.thumbnailUrl })
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Error al subir la imagen'
      )
    } finally {
      setIsUploading(false)
    }
  }

  const handleDeleteConfirm = async () => {
    setConfirmDelete(false)
    try {
      setIsUploading(true)
      if (onDeleteRequest) await onDeleteRequest(currentUrl)
      else await countryPageService.deleteImage(currentUrl)
      onDelete?.()
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Error al eliminar')
    } finally {
      setIsUploading(false)
    }
  }

  const handleUnsplashSelect = ({ url }) => {
    if (!url) return
    onUpload?.({ url, thumbnailUrl: url, source: 'unsplash' })
  }

  // Container styles: circular fuerza aspect 1:1 con max-w para no deformar.
  const containerStyle = circular
    ? {
        width: typeof height === 'number' ? `${height}px` : height,
        maxWidth: '100%',
        aspectRatio: '1 / 1',
      }
    : {
        width,
        height: typeof height === 'number' ? `${height}px` : height,
      }

  const initials = getInitials(fallbackName)

  return (
    <div>
      {label && (
        <p className="text-caption text-neutral-500 dark:text-neutral-400 mb-1.5">{label}</p>
      )}

      <div
        {...propsZona}
        style={containerStyle}
        className={cn(
          'group relative overflow-hidden flex items-center justify-center transition-all duration-fast',
          circular ? 'rounded-full' : 'rounded-md',
          currentUrl ? 'border border-line' : 'border-2 border-dashed',
          isDragOver
            ? 'border-primary bg-primary-50 dark:bg-primary-900/40'
            : currentUrl
              ? 'bg-card'
              : 'border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60',
          isUploading || disabled
            ? 'cursor-default'
            : 'cursor-pointer hover:border-primary-400 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/40',
          disabled && 'opacity-50'
        )}
      >
        {isUploading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/80">
            <Loader2 size={28} className="animate-spin text-primary" />
          </div>
        )}

        {currentUrl ? (
          <>
            <img
              src={currentUrl}
              alt={label || 'Imagen'}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 z-[2] flex items-start justify-end gap-1 bg-neutral-900/40 p-2 opacity-0 transition-opacity duration-fast group-hover:opacity-100 [@media(hover:none)]:opacity-100">
              {enableUnsplash && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setUnsplashOpen(true)
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-card text-primary shadow-sm hover:bg-primary hover:text-white transition-colors"
                  aria-label="Elegir de Unsplash"
                >
                  <Images size={15} />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setConfirmDelete(true)
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-danger text-white shadow-sm hover:bg-danger-600 transition-colors"
                aria-label="Eliminar imagen"
              >
                <X size={15} />
              </button>
            </div>
          </>
        ) : circular && fallbackName ? (
          // Fallback con iniciales para avatares (CountryHubEditor, perfil)
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-accent-100 dark:bg-accent-900/40 text-accent-700 dark:text-accent-300">
            <span className="font-display text-3xl font-semibold leading-none">
              {initials}
            </span>
            <span className="text-caption text-accent-700 dark:text-accent-300/70">Sube tu foto</span>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1.5 px-3 text-center">
            <UploadCloud size={28} className="text-neutral-400 dark:text-neutral-500" />
            <p className="text-body-sm font-medium text-neutral-700 dark:text-neutral-300">{emptyText}</p>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">{helperText}</p>
            {enableUnsplash && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  setUnsplashOpen(true)
                }}
                className="mt-1.5"
              >
                <Images size={14} /> Elegir de Unsplash
              </Button>
            )}
          </div>
        )}
      </div>

      <input {...propsInput} />

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar imagen</DialogTitle>
            <DialogDescription>
              Esta acción eliminará la imagen permanentemente. ¿Continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {enableUnsplash && unsplashOpen && (
        <Suspense fallback={null}>
          <UnsplashPicker
            open={unsplashOpen}
            onClose={() => setUnsplashOpen(false)}
            onSelect={handleUnsplashSelect}
            defaultCategory={unsplashCategory}
          />
        </Suspense>
      )}
    </div>
  )
}

export default ImageUploader
